import { DOCUMENT_REVIEW_STATUS } from "src/constants/common";
import {
  Milestone,
  MilestoneBenefit,
  MilestoneBenefitAction,
  MilestoneDocument,
  MilestoneStatus,
} from "src/types/program.types";

// Splits "Month 3" -> { label: "Month", value: "3" } so it can render as
// a calendar-style chip (header strip + big number), Gmail date-chip style.
export const parsePeriod = (period: string) => {
  const match = period.match(/^(.*?)(\d+)$/);
  if (!match) {
    return { label: period, value: "" };
  }
  return { label: match[1].trim(), value: match[2] };
};

export const MILESTONE_STATUS: Record<MilestoneStatus, MilestoneStatus> = {
  LOCKED: "LOCKED",
  DOCUMENT_UPLOAD_REQUIRED: "DOCUMENT_UPLOAD_REQUIRED",
  READY_TO_AVAIL: "READY_TO_AVAIL",
  AVAILED: "AVAILED",
};

// Sub-states of DOCUMENT_UPLOAD_REQUIRED, derived from `milestone.documents`:
// nothing uploaded yet -> UPLOAD_REQUIRED, everything in with the reviewer ->
// UNDER_REVIEW, anything bounced back -> REJECTED.
export const MILESTONE_DOC_STATE = {
  UPLOAD_REQUIRED: "UPLOAD_REQUIRED",
  UNDER_REVIEW: "UNDER_REVIEW",
  REJECTED: "REJECTED",
} as const;

export type MilestoneDocState =
  (typeof MILESTONE_DOC_STATE)[keyof typeof MILESTONE_DOC_STATE];

export const isMilestoneLocked = (milestone: Milestone) =>
  milestone.status === MILESTONE_STATUS.LOCKED;

export const isMilestoneAvailed = (milestone: Milestone) =>
  milestone.status === MILESTONE_STATUS.AVAILED;

export const needsMilestoneDocuments = (milestone: Milestone) =>
  milestone.status === MILESTONE_STATUS.DOCUMENT_UPLOAD_REQUIRED;

// In the AVAILED state every benefit is availed regardless of what the
// per-benefit flags say; everywhere else `touched`/`availed` drive the UI.
export const isBenefitAvailed = (
  milestone: Milestone,
  benefit: { touched?: boolean; availed?: boolean },
) =>
  isMilestoneAvailed(milestone) || Boolean(benefit.availed || benefit.touched);

// Document status is folded onto the four known values before anything is
// compared, since older responses spell it differently ("pending"). A bare
// "pending" means a record exists and is with the reviewer — only an explicit
// "Upload Pending" means nothing has been uploaded. Anything unrecognised also
// lands on Under Review, which is the safe default: it never tells the user a
// document is cleared or bounced when we don't know.
export const normalizeDocumentStatus = (status?: string | null): string => {
  const value = (status || "").trim().toLowerCase().replace(/[_-]+/g, " ");

  if (value === "upload pending") return DOCUMENT_REVIEW_STATUS.UPLOAD_PENDING;
  if (value === "rejected") return DOCUMENT_REVIEW_STATUS.REJECTED;
  if (value === "approved") return DOCUMENT_REVIEW_STATUS.APPROVED;
  return DOCUMENT_REVIEW_STATUS.UNDER_REVIEW;
};

// A milestone can list a document before anything is uploaded against it, so
// presence in `documents` isn't proof of an upload — the status is.
export const isDocumentUploaded = (document: MilestoneDocument) =>
  normalizeDocumentStatus(document.status) !==
  DOCUMENT_REVIEW_STATUS.UPLOAD_PENDING;

export const getMilestoneDocState = (
  milestone: Milestone,
): MilestoneDocState => {
  const documents = milestone.documents || [];

  if (
    documents.some(
      (document) =>
        normalizeDocumentStatus(document.status) ===
        DOCUMENT_REVIEW_STATUS.REJECTED,
    )
  ) {
    return MILESTONE_DOC_STATE.REJECTED;
  }

  // Anything rejected is handled above; here we only need to know whether the
  // uploads so far cover every doc the milestone asks for.
  const uploaded = documents.filter(isDocumentUploaded);
  const requiredCount = milestone.requiredDocs?.length || 0;
  if (uploaded.length === 0 || uploaded.length < requiredCount) {
    return MILESTONE_DOC_STATE.UPLOAD_REQUIRED;
  }

  return MILESTONE_DOC_STATE.UNDER_REVIEW;
};

export const getMilestoneDocCopy = (milestone: Milestone) => {
  const docState = getMilestoneDocState(milestone);
  const requiredDocs = milestone.requiredDocs || [];

  if (docState === MILESTONE_DOC_STATE.REJECTED) {
    return {
      docState,
      title: "Document Rejected",
      description:
        "One of the document uploaded is rejected, kindly re-upload the correct document(s)",
      buttonLabel: "Re-Upload",
    };
  }

  if (docState === MILESTONE_DOC_STATE.UNDER_REVIEW) {
    return {
      docState,
      title: "Documents Under Review",
      description: "We are reviewing the uploaded documents",
      buttonLabel: "View Documents",
    };
  }

  // Name only what's still missing, so a milestone with one of two documents
  // already in doesn't ask for both again.
  const missingDocs = getMissingRequiredDocs(milestone);
  const docsToUpload = missingDocs.length ? missingDocs : requiredDocs;

  return {
    docState,
    title: "Documents Required",
    description: docsToUpload.length
      ? `Upload ${formatDocList(docsToUpload)} to avail ${
          milestone.benefits.length > 1 ? "these benefits" : "this benefit"
        }`
      : "Upload the required documents to avail this benefit",
    buttonLabel: "Upload",
  };
};

// Required docs with no upload against them yet, matched label ("Purchase
// Invoice") to testName ("Invoice") on shared words.
export const getMissingRequiredDocs = (milestone: Milestone) => {
  const uploaded = (milestone.documents || []).filter(isDocumentUploaded);

  return (milestone.requiredDocs || []).filter(
    (label) =>
      !uploaded.some((document) => sharesWord(label, document.testName)),
  );
};

// "Purchase Invoice" -> "Purchase Invoice"
// ["Purchase Invoice", "Doctor Prescription"] -> "Purchase Invoice & Doctor Prescription"
export const formatDocList = (docs: string[]) =>
  docs.length <= 1
    ? docs[0] || ""
    : `${docs.slice(0, -1).join(", ")} & ${docs[docs.length - 1]}`;

// Where a benefit's button takes the user. The API sends the type on each
// action; anything physical (kits, hampers, strips, the BCA scale) needs a
// delivery address, consultations go to the booking flow.
export const BENEFIT_ACTION_TYPE = {
  ADDRESS_COLLECTION: "ADDRESS_COLLECTION",
  NAVIGATE_TO_APPOINTMENTS: "NAVIGATE_TO_APPOINTMENTS",
} as const;

export type BenefitActionType =
  (typeof BENEFIT_ACTION_TYPE)[keyof typeof BENEFIT_ACTION_TYPE];

// "ADRRESS_COLLECTION" is how the API currently spells it — accept both so a
// fix on their side doesn't take the button down with it.
const BENEFIT_ACTION_TYPE_ALIASES: Record<string, BenefitActionType> = {
  ADRRESS_COLLECTION: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ADDRESS_COLLECTION: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  NAVIGATE_TO_APPOINTMENTS: BENEFIT_ACTION_TYPE.NAVIGATE_TO_APPOINTMENTS,
};

export const resolveBenefitActionType = (
  type: string,
): BenefitActionType | null =>
  BENEFIT_ACTION_TYPE_ALIASES[(type || "").toUpperCase()] ?? null;

// Legacy benefit names, from before `actions` existed. Only used to synthesise
// an action for a response that still sends `name`/`buttonLabel`.
const LEGACY_BENEFIT_ACTION_TYPES: Record<string, BenefitActionType> = {
  ENABLE_NUTRITIONIST: BENEFIT_ACTION_TYPE.NAVIGATE_TO_APPOINTMENTS,
  ENABLE_DIET_CONSULT: BENEFIT_ACTION_TYPE.NAVIGATE_TO_APPOINTMENTS,
  ENABLE_DIETICIAN: BENEFIT_ACTION_TYPE.NAVIGATE_TO_APPOINTMENTS,
  ENABLE_PHYSIOTHERAPIST: BENEFIT_ACTION_TYPE.NAVIGATE_TO_APPOINTMENTS,
  ENABLE_SUCCESS_COACH: BENEFIT_ACTION_TYPE.NAVIGATE_TO_APPOINTMENTS,
  ENABLE_ENROLLMENT_KIT: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_ENROLMENT_KIT: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_FITNESS_KIT: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_WELLNESS_HAMPER: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_BCA: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_LIPAGLYN_STRIP_1: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_LIPAGLYN_STRIP_2: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_FREE_STRIP_M2: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
  ENABLE_FREE_STRIP_M4: BENEFIT_ACTION_TYPE.ADDRESS_COLLECTION,
};

// The buttons on a benefit come from `actions`. A response that predates it
// (name + buttonLabel) is folded into the same shape so there's one render
// path.
export const getBenefitActions = (
  benefit: MilestoneBenefit,
): MilestoneBenefitAction[] => {
  if (benefit.actions?.length) return benefit.actions;

  const legacyType = benefit.name
    ? LEGACY_BENEFIT_ACTION_TYPES[benefit.name]
    : undefined;
  if (!legacyType) return [];

  return [
    {
      label: benefit.buttonLabel || "Avail",
      type: legacyType,
      params: null,
    },
  ];
};

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z]/g, "");

// "Doctor Prescription" vs "Prescriptions", "Purchase Invoice" vs "Invoice" —
// the same document under two spellings, so compare on shared words instead of
// whole strings. Short words are dropped to keep the match from firing on
// filler like "of" or "the".
const sharesWord = (label: string, ...candidates: string[]) => {
  const haystack = candidates.map((value) => normalize(value || "")).join(" ");

  return (label || "")
    .split(/[^a-zA-Z]+/)
    .map((word) => normalize(word))
    .filter((word) => word.length >= 4)
    .some((word) => haystack.includes(word));
};

// The API sends human labels ("Doctor Prescription", "Purchase Invoice") while
// the program config keys documents by `document_type` ("Prescriptions",
// "Invoice"). Match on shared words rather than exact strings so small wording
// differences on either side don't break the mapping.
export const matchRequiredDocConfig = <
  T extends { title: string; document_type: string },
>(
  requiredDocLabel: string,
  configDocuments: T[],
): T | undefined => {
  const label = normalize(requiredDocLabel);

  const exact = configDocuments.find(
    (document) =>
      normalize(document.title) === label ||
      normalize(document.document_type) === label,
  );
  if (exact) return exact;

  return configDocuments.find((document) =>
    sharesWord(requiredDocLabel, document.title, document.document_type),
  );
};
